import { useState, useEffect, useCallback } from 'react';
import { projectsApi } from '@/api/projects';

interface ProjectStatus {
  projectStatus: string;
  containers: Array<{
    name: string;
    status: string;
    health?: string;
  }>;
}

interface UseProjectStatusOptions {
  /** Polling interval in milliseconds (default: 10000) */
  pollInterval?: number;
  /** Whether to enable polling (default: true) */
  enabled?: boolean;
}

/**
 * Hook to fetch real-time project status from Docker containers.
 * Polls the status endpoint at regular intervals.
 */
export function useProjectStatus(
  slug: string | undefined,
  options: UseProjectStatusOptions = {}
) {
  const { pollInterval = 10000, enabled = true } = options;
  const [status, setStatus] = useState<ProjectStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!slug || !enabled) return;

    try {
      const data = await projectsApi.getStatus(slug);
      setStatus(data);
      setError(null);
    } catch (err) {
      // Silently handle errors for polling - don't overwrite existing status
      setError(err instanceof Error ? err : new Error('Failed to fetch status'));
    }
  }, [slug, enabled]);

  // Initial fetch
  useEffect(() => {
    if (!slug || !enabled) return;

    setIsLoading(true);
    fetchStatus().finally(() => setIsLoading(false));
  }, [slug, enabled, fetchStatus]);

  // Polling
  useEffect(() => {
    if (!slug || !enabled || pollInterval <= 0) return;

    const intervalId = setInterval(fetchStatus, pollInterval);
    return () => clearInterval(intervalId);
  }, [slug, enabled, pollInterval, fetchStatus]);

  return {
    status,
    projectStatus: status?.projectStatus || null,
    containers: status?.containers || [],
    isLoading,
    error,
    refetch: fetchStatus,
  };
}

/**
 * Hook to fetch status for multiple projects.
 * Useful for dashboard where we need status for many projects.
 */
export function useMultipleProjectStatuses(
  slugs: string[],
  options: UseProjectStatusOptions = {}
) {
  const { pollInterval = 15000, enabled = true } = options;
  const [statuses, setStatuses] = useState<Record<string, ProjectStatus>>({});
  const [isLoading, setIsLoading] = useState(false);

  const fetchAllStatuses = useCallback(async () => {
    if (!enabled || slugs.length === 0) return;

    const results: Record<string, ProjectStatus> = {};

    // Fetch statuses in parallel with a limit to avoid overwhelming the server
    const batchSize = 5;
    for (let i = 0; i < slugs.length; i += batchSize) {
      const batch = slugs.slice(i, i + batchSize);
      const batchResults = await Promise.allSettled(
        batch.map(async (slug) => {
          const data = await projectsApi.getStatus(slug);
          return { slug, data };
        })
      );

      batchResults.forEach((result) => {
        if (result.status === 'fulfilled') {
          results[result.value.slug] = result.value.data;
        }
      });
    }

    setStatuses(results);
  }, [slugs, enabled]);

  // Initial fetch
  useEffect(() => {
    if (!enabled || slugs.length === 0) return;

    setIsLoading(true);
    fetchAllStatuses().finally(() => setIsLoading(false));
  }, [slugs.join(','), enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Polling
  useEffect(() => {
    if (!enabled || slugs.length === 0 || pollInterval <= 0) return;

    const intervalId = setInterval(fetchAllStatuses, pollInterval);
    return () => clearInterval(intervalId);
  }, [slugs.join(','), enabled, pollInterval]); // eslint-disable-line react-hooks/exhaustive-deps

  const getStatus = useCallback(
    (slug: string) => statuses[slug]?.projectStatus || null,
    [statuses]
  );

  return {
    statuses,
    getStatus,
    isLoading,
    refetch: fetchAllStatuses,
  };
}
